// src/oura/hooks/useClientBadges.ts
//
// The counts on the merchant's nav items: To do (asks and documents
// waiting on them) and Reports (reports they have not opened).
//
// Both come out of ONE read of the shared client page, which is the reason
// they live in one hook. A second hook for the Reports count would have been
// a second request for a payload already in hand, on every page load.
//
// Read from the shared client page rather than fetched on its own, so the
// badge costs no extra request (the shell's bell and the screen read the same
// cached payload). The cache lives a few seconds, which alone would leave the
// badge showing an ask the merchant has just answered; every write path calls
// invalidateClientPage(), and that tells this hook to read again. Documents
// change without the merchant doing anything (a doc shared, a question
// answered in the doc), so the page is also read again while it carries
// documents (useMerchantDocuments.ts).

import { useEffect, useState } from "react";
import type { View } from "../nav";
import { getClientPage, subscribeClientPage } from "@service/aeo/clientReview";
import { needsYouCount } from "../merchant/docAsks";
import { MERCHANT_V2 } from "../merchantV2";
import { setupPart, useMySetup } from "./useMySetup";
import { keepMerchantDocsFresh, noteDocsServed } from "./useMerchantDocuments";

export interface ClientBadges {
    /**
     * What waits on the client on To do: asks, shared documents and their own
     * open set-up steps.
     *
     * The rule is that the badge counts what To do's tabs list (usability
     * test, 14 Oct 2026: a badge of 10 beside a page saying 9). In the
     * redesign's five-item rail set-up is a To do tab again, so it is counted
     * here, and it adds up to the staff Overview's "Needs the client". The
     * older screens count both too (#4026 C-FE-6).
     */
    asks: number | null;
    /** For a Setup rail item; there is none in the five-item rail, so always null now. */
    setupMine: number | null;
    /**
     * Whether `asks` is the whole story. False while the client's own set-up
     * read has not landed or has failed: the asks are counted, the set-up
     * steps are not, so the number is a lower bound and the label says so.
     */
    asksExact: boolean;
    /**
     * Reports the client has not opened (#4026 C-FE-1, ourteam-backend#5414
     * C-BE-1). A client could not tell that staff had sent one.
     *
     * Absent from the payload -- a server before C-BE-1 -- reads as null, the
     * same as a failed read: not known, so no badge. Never 0 on screen.
     */
    unreadReports: number | null;
    /**
     * Shared documents the client has not opened (ourteam-backend#6108).
     * Absent from the payload reads as null: not known, so no dot.
     */
    unreadDocuments: number | null;
    /**
     * "Month 3 of 12" for the rail, when the payload carries both (#4062
     * MR-FE-2). Null when there is no plan or no term -- a client who has not
     * been sold a term has no month to be in, and "Month 0 of 0" would be a
     * claim about one.
     *
     * Read here rather than with a second request: this hook already reads
     * the whole client page and was throwing the rest away.
     */
    term: { month: number; of: number } | null;
}

const NOTHING = { asks: null, unreadReports: null, unreadDocuments: null, term: null } as const;

/** Where the client is in their term, when the payload says both. */
function termOf(payload: Parameters<typeof needsYouCount>[0] & { plan?: { month?: number } | null; subscription?: { termMonths?: number } | null; schedule?: { currentMonth?: number; months?: unknown[] } | null }): { month: number; of: number } | null {
    // The schedule's month first, as every page reads it (Home's head, the
    // plan drawer): the plan's own month can lag it, and the rail said
    // "Month 3 of 12" beside a page saying "Month 2 of 12".
    const month = payload.schedule?.currentMonth ?? payload.plan?.month;
    const of = payload.schedule?.months?.length || payload.subscription?.termMonths;
    // WHOLE MONTHS, AND INSIDE THE TERM. "Month 13 of 12" and "Month 0 of 6"
    // are not facts about anything, and a sidebar that states one has told
    // the client something false with complete confidence (Jack, #4063). A
    // payload that cannot say where they are says nothing, which is the rule
    // everywhere else here.
    const whole = (n: unknown): n is number => typeof n === "number" && Number.isInteger(n) && n >= 1;
    return whole(month) && whole(of) && month <= of ? { month, of } : null;
}

/**
 * The merchant's nav counts, or null where unknown (not loaded, not a
 * merchant, or the read failed). Null draws no badge; neither is ever shown
 * as 0.
 */
export function useClientBadges(enabled = true): ClientBadges {
    const [count, setCount] = useState<{ asks: number | null; unreadReports: number | null; unreadDocuments: number | null; term: { month: number; of: number } | null }>(NOTHING);
    // The client's own open set-up steps are pending work too (#4026 C-FE-6):
    // a client with 4 asks and 2 set-up steps of their own reads 6 on Home and
    // on Decisions, so the badge has to say 6 as well. Its own route and its
    // own read state, outside the client-page effect, so a set-up read that
    // lands later updates the badge without re-reading the page.
    const setup = setupPart(useMySetup(enabled));

    useEffect(() => {
        if (!enabled) {
            setCount(NOTHING);
            return;
        }
        let cancelled = false;
        // Every invalidation starts another read, and the reads can land out
        // of order: a mark-read invalidates the cache while the first read is
        // still in flight, the fresh "0 unread" arrives, and then the stale
        // "1 unread" overwrites it and the badge is wrong until something
        // else refreshes it. Only the newest request may write.
        let issued = 0;
        const read = () => {
            const seq = ++issued;
            getClientPage().then(
                (r) => {
                    if (cancelled || seq !== issued) return;
                    if (r.ok) noteDocsServed(r.payload);
                    setCount(r.ok
                        ? {
                            asks: needsYouCount(r.payload),
                            unreadReports: typeof r.payload.unreadReports === "number" ? r.payload.unreadReports : null,
                            unreadDocuments: typeof r.payload.unreadDocuments === "number" ? r.payload.unreadDocuments : null,
                            term: termOf(r.payload),
                        }
                        : NOTHING);
                },
                // A badge is decoration: a failed read hides it rather than
                // taking the shell down with an unhandled rejection.
                () => {
                    if (!cancelled && seq === issued) setCount(NOTHING);
                },
            );
        };
        read();
        const unsubscribe = subscribeClientPage(read);
        const release = keepMerchantDocsFresh();
        return () => {
            cancelled = true;
            unsubscribe();
            release();
        };
    }, [enabled]);

    return {
        // Null stays null: an unread client page means the asks are not known,
        // and set-up steps alone are not "what needs you".
        // Set-up is a To do tab again (the five-item rail), so the To do badge
        // counts it: still only what To do's own tabs list. No Setup item, no
        // Setup badge.
        asks: count.asks === null ? null : count.asks + (setup.known ? setup.mine : 0),
        asksExact: count.asks !== null && setup.known,
        setupMine: null,
        unreadReports: count.unreadReports,
        unreadDocuments: count.unreadDocuments,
        term: count.term,
    };
}

/**
 * The count on a nav item, or null for no badge.
 *
 * Decisions: what waits on the client. Reports: what they have not opened
 * (#4026 C-FE-1) -- a client had no way to tell that staff had sent one.
 *
 * `null` and `0` both draw nothing, and they are different facts: null is "not
 * known" (not loaded, the read failed, or a server from before the field
 * existed), 0 is "nothing waiting". Neither is worth a badge, so they render
 * alike -- but the screen-reader label is only ever written from a real count.
 */
export function badgeFor(
    view: View,
    asks: number | null,
    unreadReports: number | null,
    asksExact = true,
    setupMine: number | null = null,
    unreadDocuments: number | null = null,
): { count: number; label: string; dot?: boolean } | null {
    if (view === "decisions" && asks) {
        // A badge can only draw a number, so an incomplete count draws the
        // lower bound -- and its label says which it is rather than claiming a
        // total it does not have.
        const need = asks === 1 ? "needs" : "need";
        return { count: asks, label: asksExact ? `${asks} ${need} you` : `at least ${asks} ${need} you` };
    }
    // Nothing needs them, but a document landed: a blue dot, not a number. The
    // amber number means "needs you", and a document to read is not that (#6108).
    // In the redesign every document lives on Documents, so a new one is
    // counted there, with the new reports (22 Oct 2026).
    if (!MERCHANT_V2 && view === "decisions" && unreadDocuments) {
        return { count: unreadDocuments, label: `${unreadDocuments} new ${unreadDocuments === 1 ? "document" : "documents"}`, dot: true };
    }
    if (view === "access" && setupMine) {
        return { count: setupMine, label: `${setupMine} set-up ${setupMine === 1 ? "step" : "steps"} left` };
    }
    if (view === "reports" && MERCHANT_V2) {
        const n = (unreadReports ?? 0) + (unreadDocuments ?? 0);
        return n > 0 ? { count: n, label: `${n} new ${n === 1 ? "document" : "documents"}` } : null;
    }
    if (view === "reports" && unreadReports) {
        return { count: unreadReports, label: `${unreadReports} new ${unreadReports === 1 ? "report" : "reports"}` };
    }
    return null;
}
